
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import PasswordInput from '../shared/PasswordInput';

function OwnerAdd() {
    const { t } = useTranslation();
    const [companyname, setCompanyName] = useState('');
    const [domain, setDomain] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [username, setUsername] = useState('');
    const [firstname, setFirstName] = useState('');
    const [lastname, setLastName] = useState('');
    const [password, setPassword] = useState('');
    // ⭐ New: Password confirmation state
    const [confirmPassword, setConfirmPassword] = useState('');
    const [registerStatus, setRegisterStatus] = useState('');
    // ⭐ New: Password match validation state
    const [passwordError, setPasswordError] = useState('');

    const navigate = useNavigate();

    const baseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:5000';

    /**
     * ⭐ New: Validate password confirmation in real-time
     * This provides immediate feedback to the user as they type
     */
    const validatePasswordMatch = (pass, confirm) => {
        if (confirm && pass !== confirm) {
            setPasswordError(t('auth.passwordsNotMatchError'));
            return false;
        } else {
            setPasswordError('');
            return true;
        }
    };

    /**
     * ⭐ Updated: Handle password change with validation
     */
    const handlePasswordChange = (e) => {
        const newPassword = e.target.value;
        setPassword(newPassword);
        // Validate against confirmation if it exists
        if (confirmPassword) {
            validatePasswordMatch(newPassword, confirmPassword);
        }
    };

    /**
     * ⭐ New: Handle confirm password change with validation
     */
    const handleConfirmPasswordChange = (e) => {
        const newConfirm = e.target.value;
        setConfirmPassword(newConfirm);
        validatePasswordMatch(password, newConfirm);
    };

    const registerowner = async (e) => {
        e.preventDefault();

        // ⭐ New: Validate password confirmation before submission
        if (password !== confirmPassword) {
            setPasswordError(t('auth.passwordsNotMatchError'));
            setRegisterStatus(t('auth.passwordMismatchWarning'));
            return;
        }

        // ⭐ New: Validate minimum password length (optional but recommended)
        if (password.length < 8) {
            setPasswordError(t('auth.passwordTooShortError'));
            setRegisterStatus(t('auth.passwordTooShortMessage'));
            return;
        }

        try {
                const response = await axios.post(`${baseUrl}/api/tenants/registerowner`, {
                companyname,
                domain,
                username,
                email,
                phone,
                password,
                firstname,
                lastname,
            });

            if (response.status === 201) {
                setRegisterStatus(t('auth.accountCreatedSuccess'));
                // setTimeout(() => navigate('/ownerlogin'), 0);
                navigate('/ownerlogin');
            } else if (response.data.message) {
                setRegisterStatus(response.data.message);
                navigate('/ownerlogin');
            }
        } catch (error) {
            setRegisterStatus(t('auth.registrationError'));
            console.error('Error during registration:', error);
        }
    };

    return (
        // <div className="owner-add-container" style={{ paddingTop: '80px', paddingBottom: '120px' }}>
        <div className="owner-add-container">
            <div className="card">
                <h4 className="hdr hdr--primary hdr--lg">{t('auth.createAccountTitle')}</h4>
                <div className="card-body">
                    <form onSubmit={registerowner} className="form">
                        <div className="form-group">
                            <label htmlFor="companyname">{t('tenant.companyNameLabel')}</label>
                            <input
                                type="text"
                                id="companyname"
                                value={companyname}
                                onChange={(e) => setCompanyName(e.target.value)}
                                placeholder={t('tenant.companyNamePlaceholder')}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="domain">{t('tenant.domainLabel')}</label>
                            <input
                                type="text"
                                id="domain"
                                value={domain}
                                onChange={(e) => setDomain(e.target.value)}
                                placeholder={t('tenant.domainPlaceholder')}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="email">{t('auth.emailLabel')}</label>
                            <input
                                type="email"
                                id="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder={t('auth.emailAddress')}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="phone">{t('auth.phoneLabel')}</label>
                            <input
                                type="text"
                                id="phone"
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                placeholder={t('auth.phoneNumber')}
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="username">{t('auth.usernameLabel')}</label>
                            <input
                                type="text"
                                id="username"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                placeholder={t('auth.usernamePlaceholder')}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="firstname">{t('auth.firstNameLabel')}</label>
                            <input
                                type="text"
                                id="firstname"
                                value={firstname}
                                onChange={(e) => setFirstName(e.target.value)}
                                placeholder={t('auth.firstNamePlaceholder')}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="lastname">{t('auth.lastNameLabel')}</label>
                            <input
                                type="text"
                                id="lastname"
                                value={lastname}
                                onChange={(e) => setLastName(e.target.value)}
                                placeholder={t('auth.lastNamePlaceholder')}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="password">{t('auth.passwordLabel')}</label>
                            <PasswordInput
                                id="password"
                                value={password}
                                onChange={handlePasswordChange}
                                placeholder={t('auth.passwordPlaceholder')}
                                required
                                minLength={8}
                            />
                            {/* ⭐ New: Show password requirements hint */}
                            <small className="form-text text-muted">
                                {t('auth.passwordRequirementsHint')}
                            </small>
                        </div>
                        {/* ⭐ New: Password confirmation field */}
                        <div className="form-group">
                            <label htmlFor="confirmPassword">{t('auth.confirmPasswordLabel')}</label>
                            <PasswordInput
                                id="confirmPassword"
                                value={confirmPassword}
                                onChange={handleConfirmPasswordChange}
                                placeholder={t('auth.reenterPasswordPlaceholder')}
                                required
                                minLength={8}
                                className={passwordError ? 'error' : ''}
                            />
                            {/* ⭐ New: Display password match error in real-time */}
                            {passwordError && (
                                <small className="form-text text-danger">
                                    {passwordError}
                                </small>
                            )}
                            {/* ⭐ New: Display success indicator when passwords match */}
                            {confirmPassword && !passwordError && password === confirmPassword && (
                                <small className="form-text text-success">
                                    {t('auth.passwordsMatchSuccess')}
                                </small>
                            )}
                        </div>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            // ⭐ New: Disable submit if passwords don't match
                            disabled={password !== confirmPassword || passwordError || !password}
                        >
                            {t('auth.createOwnerButton')}
                        </button>
                        <button onClick={() => navigate('/ownerlogin')} className="btn btn-secondary">
                            {t('auth.ownerLoginButton')}
                        </button>
                        <button onClick={() => navigate(-1)} className="btn btn-secondary">
                            {t('common.back')}
                        </button>
                        {registerStatus && <p className="status-message">{registerStatus}</p>}
                    </form>
                </div>
            </div>
        </div>
    );
}

export default OwnerAdd;
